import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useFormContext, useWatch} from 'react-hook-form'
import {Column} from 'react-table'
import {KTable, KTableHeader} from '../../../../../../components/UI'
import {Select} from '../../../../../../components/UI/Select'
import {KTableProvider} from '../../../../../../contexts/KTableProvider'
import {useCentral} from '../../../../../../hooks/useCentral'
import {SelectOption} from '../../../../../../models/utility.model'
import {useCentralServices} from '../../../../../hoteleria/hooks/useCentralServices'
import {getRangeParamsById} from '../../../../services'
import {TabCentralModel} from '../../../../../../models'
import ButtonClose from '../../../../../../components/UI/Button/IconButton'

const EditableCell = React.memo(
  ({
    columnId,
    value,
    onChange,
  }: {
    columnId: string
    value: number
    onChange: (event: React.ChangeEvent<HTMLInputElement>, row: any) => void
  }) => (
    <input
      type='number'
      name={columnId}
      value={value}
      onChange={onChange as any}
      onFocus={(event) => event.target.select()}
      step='0.01'
      onKeyPress={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
        }
      }}
      style={{
        width: '100%',
        border: 'none',
        backgroundColor: '#eff2f5',
        height: '40px',
        textAlign: 'center',
        minWidth: '80px',
      }}
    />
  ),
)

const Index: FC = () => {
  const {
    setError,
    register,
    formState: {errors},
    control,
    setValue,
    watch,
  } = useFormContext()

  const {currentTabItem, changeCurrentTabItem} = useCentral()
  const [compani, setCompany] = useState<SelectOption[]>([])
  const [client, setClient] = useState<SelectOption[]>([])
  const [sites, setSites] = useState<SelectOption[]>([])
  const [camps, setCamps] = useState<SelectOption[]>([])
  const [applications, setApplications] = useState<SelectOption[]>([])
  const {getCompanies, getClients, getApplications, getSites, getCamps} = useCentralServices()
  const [datos, setDatos] = useState<any[]>([])
  const rangosW = useWatch({name: 'Rangos'})

  const getCompaniesFn = async () => {
    const data = await getCompanies({isCombo: true}, 1)
    setCompany(data)
  }

  const getClientsFn = async (companyId: string) => {
    const data = await getClients({isCombo: true, companiaId: companyId}, 1)
    setClient(data)
  }

  const getSitesFn = async (clientId: string) => {
    const data = await getSites({isCombo: true, clienteId: clientId}, 1)
    setSites(data)
  }

  const getCampsFn = async (id: string) => {
    const data = await getCamps({isCombo: true, siteId: id}, 1)
    if (data.length === 1) {
      setValue('CampamentoId', data[0].value as string)
      setError('CampamentoId', {})
    }
    const allData = [{value: '4EsB2NAAsDA=', label: 'TODOS'}, ...data]
    setCamps(allData)
  }

  const getApplicationsFn = async () => {
    const data = await getApplications()
    const filterData = data.filter(
      (item) =>
        item.label === 'Hotelería' || item.label === 'Lavandería' || item.label === 'Comedores',
    )
    setApplications(filterData)
  }

  const getRangeParamId = async () => {
    try {
      const {idToEdit} = currentTabItem
      if (idToEdit) {
        const {
          data: {data},
        } = await getRangeParamsById(idToEdit as any)
        const {Id, CompaniaId, ClienteId, SiteId, AplicacionId, Rangos, CampamentoId} = data
        setValue('Id', Id)
        setValue('CompaniaId', CompaniaId)
        setValue('ClienteId', ClienteId)
        setValue('AplicacionId', AplicacionId)
        setValue('SiteId', SiteId)
        setValue('CampamentoId', CampamentoId)
        setValue('AplicacionId', AplicacionId)
        getClientsFn(CompaniaId as any)
        getSitesFn(ClienteId as any)
        getCampsFn(SiteId as any)
        const dataTr = transformBack(Rangos)
        setDatos(dataTr)
      }
    } catch (error) {}
  }

  useEffect(() => {
    if (currentTabItem.idToEdit) {
      getRangeParamId()
    }
  }, [currentTabItem.idToEdit])

  useEffect(() => {
    getCompaniesFn()
    getApplicationsFn()
    const initialRangeParameters = ['Minimo', 'Maximo'].map((Rango) => ({
      Rango,
      ...Object.fromEntries(Array.from({length: 51}, (_, i) => [`R${i}`, 0])),
    }))
    setDatos(initialRangeParameters)
    setValue('Rangos', transformArray(initialRangeParameters))
  }, [])

  useEffect(() => {
    //Limpiar tabla
    if (rangosW) {
      const initialRangeParameters = ['Minimo', 'Maximo'].map((Rango) => ({
        Rango,
        ...Object.fromEntries(Array.from({length: 51}, (_, i) => [`R${i}`, 0])),
      }))
      const transf = transformArray(initialRangeParameters)
      if (JSON.stringify(transf) === JSON.stringify(rangosW)) {
        setDatos(initialRangeParameters)
      }
    }
  }, [rangosW])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, row: any) => {
    setDatos((prev) => {
      const newData = prev.length === 0 ? [{}] : [...prev]
      newData[row.index][event.target.name] = Number(event.target.value) || 0
      setValue('Rangos', transformArray(newData))
      return newData
    })
  }

  let isDisabled = currentTabItem.idToEdit ? true : false

  const handleGetClientList = async (val: any) => {
    let value = val ? val.value : ''
    getClientsFn(value)
    setValue('CompaniaId', value)
    setError('CompaniaId', {})
    setValue('ClienteId', '')
    setValue('SiteId', '')
    setSites([])
    setClient([])
  }

  const handleGetSiteList = async (val: any) => {
    let value = val ? val.value : ''
    getSitesFn(value)
    setValue('ClienteId', value)
    setError('ClienteId', {})
    setValue('SiteId', '')
    setSites([])
  }

  const handleGetCampList = async (val: any) => {
    let value = val ? val.value : ''
    getCampsFn(value)
    setValue('SiteId', value)
    setError('SiteId', {})
    setValue('CampamentoId', '')
    setCamps([])
  }

  const transformArray = (arr: any[]) => {
    const result = []

    for (let i = 0; i <= 50; i++) {
      const codigo = `R${i}`
      result.push({
        Codigo: codigo,
        Minimo: arr[0][codigo], // Valor de "Minimo" del primer objeto
        Maximo: arr[1][codigo], // Valor de "Maximo" del segundo objeto
      })
    }

    return result
  }

  const transformBack = (arr: any) => {
    // Inicializar objetos para "Minimo" y "Maximo"
    const minimoObj: any = {Rango: 'Minimo'}
    const maximoObj: any = {Rango: 'Maximo'}

    // Iterar sobre el array de entrada
    arr.forEach((item: any) => {
      const {Codigo, Minimo, Maximo} = item
      minimoObj[Codigo] = Minimo // Asignar valores al objeto "Minimo"
      maximoObj[Codigo] = Maximo // Asignar valores al objeto "Maximo"
    })

    // Devolver un array con ambos objetos
    return [minimoObj, maximoObj]
  }

  const columns: Column<any>[] = useMemo(() => {
    const baseColumns: Column<any>[] = [
      {
        Header: (props) => <KTableHeader tableProps={props} title={''} />,
        accessor: 'Rango',
      },
    ]

    const rangeColumns: Column<any>[] = Array.from({length: 51}, (_, index) => ({
      Header: (props) => <KTableHeader tableProps={props} title={`R${index}`} />,
      accessor: `R${index}`,
      Cell: ({...props}) => (
        <EditableCell
          columnId={props.column.id}
          value={props.value}
          onChange={(event) => handleInputChange(event, props.row)}
        />
      ),
    }))

    return [...baseColumns, ...rangeColumns]
  }, [])

  const handleBack = () => {
    const obj: TabCentralModel = {
      ...currentTabItem,
      showPanel: false,
    }
    changeCurrentTabItem(obj)
  }

  const handleModuleChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('AplicacionId', value)
    setError('AplicacionId', {})
  }

  return (
    <div className='sdx_form card p-4'>
      {isDisabled && <ButtonClose onClick={handleBack} color='#283897' />}
      <h4>Datos de Rangos de Parametros</h4>
      <div className='row'>
        <div className='col-sm-12 col-md-4 col-xl-3'>
          <label className='required'>Compañía</label>
          <Select
            control={control}
            name='CompaniaId'
            data={compani}
            errors={errors}
            disabled={isDisabled}
            onChangeValue={handleGetClientList}
          />
        </div>

        <div className='col-sm-12 col-md-4 col-xl-3'>
          <label className='required'>Cliente</label>
          <Select
            control={control}
            name='ClienteId'
            data={client}
            errors={errors}
            disabled={isDisabled}
            onChangeValue={handleGetSiteList}
          />
        </div>
        <div className='col-sm-12 col-md-4 col-xl-3'>
          <label className='required'>Site</label>
          <Select
            control={control}
            name='SiteId'
            data={sites}
            errors={errors}
            disabled={isDisabled}
            onChangeValue={handleGetCampList}
          />
        </div>
        <div className='col-sm-12 col-md-4 col-xl-3'>
          <label className='required'>Campamento</label>
          <Select
            control={control}
            name='CampamentoId'
            data={camps}
            errors={errors}
            disabled={isDisabled}
          />
        </div>
        <div className='col-sm-12 col-md-4 col-xl-3'>
          <label className='required'>Módulo</label>
          <Select
            control={control}
            name='AplicacionId'
            data={applications}
            errors={errors}
            disabled={isDisabled}
            onChangeValue={handleModuleChange}
          />
        </div>
      </div>
      <div className='row'>
        <KTableProvider>
          <KTable data={datos} columns={columns} showPagination={false} />
        </KTableProvider>
      </div>
    </div>
  )
}

export default Index
